import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateUnprocessedTranscriberFaxList,
  updateTranscriberIndexOnList,
  resetError,
} from "./TranscriberList.actions";
import withRouter from "utils/withRouter";
import ResetListState from "components/ResetListState";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import { TranscriptionConstants } from "lookup/TranscriptionConstants";

import { formatDateForDisplay } from "services/MomentUtils";
import { showNotification } from "services/NotificationService";
import ServerSideReactTable from 'commons/table/ServerSideReactTable';
import { FilterTypes, ColumnTypes } from 'commons/table/Constants';
import { useNavigate } from "react-router-dom";

const Tip = (props) => (
  <div style={{ textAlign: "center", color: "#888", fontSize: "0.9em" }}>
    <em>{props.children}</em>
  </div>
);

const DEFAULT_PAGE_SIZE = 15;

const TranscriberList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Get state from Redux using useSelector
  const reduxData = useSelector((state) => 
    state.transcriberList.data !== undefined ? state.transcriberList.data : []
  );
  const reduxPages = useSelector((state) => 
    state.transcriberList.pages ? state.transcriberList.pages : 0
  );
  const reduxTotalElements = useSelector((state) =>
    state.transcriberList.totalElements !== undefined
      ? state.transcriberList.totalElements
      : "Loading ..."
  );
  const reduxLoading = useSelector((state) => state.transcriberList.loading);
  const reduxIsError = useSelector((state) => state.transcriberList.isError);
  const reduxListState = useSelector((state) => {
    let listState = state.transcriberList.listState;
    if (!listState) {
      listState = {
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE,
        sorted: TranscriptionConstants.DEFAULT_TRANSCRIBED_FAX_LIST_SORTING,
        filtered: TranscriptionConstants.DEFAULT_TRANSCRIBED_FAX_LIST_FILTER,
        resized: [],
        expanded: {},
      };
    }
    return listState;
  });

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(reduxData);
  const [isError, setIsError] = useState(false);
  const [pages, setPages] = useState(reduxPages);
  const [totalElements, setTotalElements] = useState(reduxTotalElements);
  const [listState, setListState] = useState(reduxListState);
  
  const updateData = useCallback((state, preventUpdate) => {
    if (!preventUpdate) {
      if (
        state.sorted !==
        TranscriptionConstants.DEFAULT_TRANSCRIBED_FAX_LIST_SORTING
      )
        state.sorted.forEach((element, i) => {
          element.isDefault = false;
        });

      dispatch(updateUnprocessedTranscriberFaxList(
        state.pageSize,
        state.page,
        state.sorted,
        state.filtered,
        state.resized,
        state.expanded
      ));
    } else {
      setListState(state);
    }
  }, [dispatch]);

  const onUpdateTable = useCallback((filterValues, sortBy, pageSize, pageNo) => {
    const newListState = { ...listState, pageSize, page: pageNo, sorted: sortBy ? sortBy : [], filtered: filterValues };
    updateData(newListState);
  }, [listState, updateData]);

  useEffect(() => {
    updateData(listState);
  }, []);

  useEffect(() => {
    if (reduxData !== data) {
      setData(reduxData);
    }
    if (reduxPages !== pages) {
      setPages(reduxPages);
    }
    if (reduxTotalElements !== totalElements) {
      setTotalElements(reduxTotalElements);
    }
    if (reduxListState !== listState) {
      setListState(reduxListState);
    }
    if (reduxLoading !== loading) {
      setLoading(reduxLoading);
    }

    if (isError === true && reduxIsError !== isError) {
      showNotification({
        title: "Error Loading Data!",
        message: "You have entered invalid information.",
        position: "bl",
        type: "error",
      });
      dispatch(resetError());
    }
  }, [reduxData, reduxPages, reduxTotalElements, reduxListState, reduxLoading, reduxIsError, dispatch, data, pages, totalElements, listState, loading, isError]);


  const renderDateCell = (date) => {
    return (
      <span
        style={{ width: "100%", display: "flex", justifyContent: "center" }}
      >
        {date}
      </span>
    );
  };

  const renderEffectiveDateCol = (date) => {
    if (date) {
      return renderDateCell(formatDateForDisplay(date));
    }
    return <span className="center">N/A</span>;
  };

  const renderLastModifiedDateCol = (date) => {
    if (date) {
      return renderDateCell(formatDateForDisplay(date));
    }
    return <span className="center">N/A</span>;
  };

  const columns = [
    {
      header: "Fax Document",
      accessor: "faxFileKey",
      accessorKey: "faxFileKey",
      width: 400,
      enableSorting: false,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Associated Switch",
      accessor: "associatedSwitchId",
      accessorKey: "associatedSwitchId",
      width: 200,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Customer",
      accessor: "customer",
      accessorKey: "customer",
      width: 100,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Client",
      accessor: "client",
      accessorKey: "client",
      width: 100,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Disposition",
      accessor: "disposition",
      accessorKey: "disposition",
      width: 200,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Model Name",
      accessor: "modelName",
      accessorKey: "modelName",
      width: 200,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.WrapTextOnNoSpace,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Selected",
      accessor: "selected",
      accessorKey: "selected",
      width: 100,
      enableSorting: false,
      enableColumnFilter: true,
      columnType: ColumnTypes.Boolean,
      filterType: FilterTypes.SingleSelect,
      filterOptions: TranscriptionConstants.SELECTED_FIELD_OPTIONS,
      filterReturnKey: true,
    },
    {
      header: "Last Modified Date",
      accessor: "lastModifiedDate",
      accessorKey: "lastModifiedDate",
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => {
        return renderLastModifiedDateCol(value)
      },
      filterType: FilterTypes.DateRangePicker,
      placeDatePickerAtEnd: true
    },
    {
      header: "Received Date",
      accessor: "generatedDate",
      accessorKey: "generatedDate",
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => {
        return renderEffectiveDateCol(value)
      },
      filterType: FilterTypes.DateRangePicker,
      placeDatePickerAtEnd: true
    },
  ];
  return (
    <Container className="list-container" fluid>
      <Row>
        <Col md={12}>
          <div className="codex-version-display-bar">
            <span style={{ float: "left" }}>
              {" "}
              Total Transcribed Faxes: <strong>{totalElements}</strong>
            </span>
            <strong style={{ float: "center" }}>Transcribed Fax List</strong>
            <ResetListState
              onReset={useCallback(() => {
                updateData(
                  {
                    ...listState,
                    filtered: TranscriptionConstants.DEFAULT_TRANSCRIBED_FAX_LIST_FILTER,
                    sorted: TranscriptionConstants.DEFAULT_TRANSCRIBED_FAX_LIST_SORTING,
                  },
                  false
                );
              }, [listState, updateData])}
              resetterDisabled={
                listState.filtered ===
                TranscriptionConstants.DEFAULT_TRANSCRIBED_FAX_LIST_FILTER &&
                listState.sorted ===
                TranscriptionConstants.DEFAULT_TRANSCRIBED_FAX_LIST_SORTING
              }
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ServerSideReactTable
            columnDefination={columns}
            data={data}
            currentPage={listState.page}
            sorted={listState.sorted}
            filters={listState.filtered}
            totalPages={pages}
            totalRecords={totalElements == "loading" ? 0 : totalElements}
            loading={loading}
            enableMultiRowSelection={false}
            addRowSelection={false}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            pageSizeOptions={[5, 10, 15, 20, 30, 50]}
            onUpdateTableData={onUpdateTable}
            onRowClick={useCallback((rowInfo) => {
              if (rowInfo !== undefined) {
                dispatch(updateTranscriberIndexOnList(rowInfo.index));
                navigate(
                  `/transcription/${rowInfo.original.transcriptionInfoUuid}`
                );
              }
            }, [dispatch, navigate])}
          />
          <Tip>
            Tip: Hold shift when sorting to multi-sort and disable sort!
          </Tip>
        </Col>
      </Row>
    </Container>
  );
}

export default withRouter(TranscriberList);
